import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-scroll';
import { FaGithub, FaLinkedin, FaEnvelope, FaArrowUp } from 'react-icons/fa';
import { profile } from '../data/portfolio';
import { useI18n } from '../context/LanguageContext';

const FooterContainer = styled.footer`
  border-top: 1px solid var(--border);
  background: var(--bg-alt);
  margin-top: 2rem;
`;

const Inner = styled.div`
  max-width: var(--max-width); margin: 0 auto; padding: 3rem 2rem 2rem;
  display: flex; flex-direction: column; align-items: center; gap: 1.4rem; text-align: center;
  @media (max-width: 768px) { padding: 2.5rem 1.25rem 1.75rem; }
`;

const Brand = styled(Link)`
  display: inline-flex; align-items: center; gap: 0.5rem;
  font-family: var(--font-heading); font-weight: 700; font-size: 1.15rem; cursor: pointer;
  .mark {
    display: grid; place-items: center; width: 32px; height: 32px; border-radius: 9px;
    background: var(--gradient-accent); color: #fff; font-size: 0.9rem;
  }
`;

const Tagline = styled.p`
  color: var(--text-muted); font-size: 0.95rem; max-width: 440px;
`;

const Socials = styled.div`
  display: flex; gap: 0.75rem;
  a {
    display: grid; place-items: center; width: 44px; height: 44px; border-radius: 12px;
    border: 1px solid var(--border); background: var(--surface); color: var(--text-muted);
    font-size: 1.15rem; transition: all 0.25s ease;
  }
  a:hover { color: var(--text); border-color: var(--primary-light); transform: translateY(-3px); }
`;

const Divider = styled.div` width: 100%; height: 1px; background: var(--border); `;

const Bottom = styled.div`
  width: 100%; display: flex; align-items: center; justify-content: space-between;
  gap: 1rem; flex-wrap: wrap;
  span { color: var(--text-faint); font-size: 0.85rem; }
  @media (max-width: 768px) { justify-content: center; }
`;

const Spark = styled.span`
  cursor: pointer;
  color: var(--primary-light);
  transition: color 0.25s ease, transform 0.25s ease;
  display: inline-block;
  &:hover { color: var(--secondary); transform: rotate(90deg) scale(1.2); }
`;

const ToTop = styled(Link)`
  display: inline-flex; align-items: center; gap: 0.5rem;
  font-size: 0.85rem; color: var(--text-muted); cursor: pointer; transition: color 0.25s ease;
  .arrow {
    display: grid; place-items: center; width: 30px; height: 30px; border-radius: 50%;
    border: 1px solid var(--border-strong); transition: all 0.25s ease;
  }
  &:hover { color: var(--text); }
  &:hover .arrow { border-color: var(--primary-light); transform: translateY(-2px); }
`;

const Footer = () => {
  const { t } = useI18n();

  return (
  <FooterContainer>
    <Inner>
      <Brand to="hero" smooth duration={500} offset={-70}>
        <span className="mark">{profile.initials}</span> {profile.shortName}
      </Brand>
      <Tagline>{t.footer.tagline}</Tagline>
      <Socials>
        <motion.a whileHover={{ scale: 1.05 }} href={profile.github} target="_blank" rel="noopener noreferrer" aria-label="GitHub"><FaGithub /></motion.a>
        <motion.a whileHover={{ scale: 1.05 }} href={profile.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><FaLinkedin /></motion.a>
        <motion.a whileHover={{ scale: 1.05 }} href={`mailto:${profile.email}`} aria-label="Email"><FaEnvelope /></motion.a>
      </Socials>

      <Divider />

      <Bottom>
        <span>
          &copy; {new Date().getFullYear()} {profile.name}. {t.footer.built}{' '}
          <Spark
            role="button"
            tabIndex={0}
            title="Go on, click me 🎉"
            onClick={() => window.dispatchEvent(new Event('konami:fire'))}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && window.dispatchEvent(new Event('konami:fire'))}
          >
            ✦
          </Spark>
          .
        </span>
        <ToTop to="hero" smooth duration={500} offset={-70}>
          {t.footer.backToTop} <span className="arrow"><FaArrowUp /></span>
        </ToTop>
      </Bottom>
    </Inner>
  </FooterContainer>
  );
};

export default Footer;
